// Sidebar.js - Complete with all pages enabled + proper spacing + SHOP ID PRESERVED
// ✅ Feedback dropdown removed
// ✅ Separate "Expense" dropdown removed
// ✅ Postings MOVED from Communication -> Finance
// ✅ "Suppliers" field MOVED from Suppliers dropdown -> Finance
// ✅ Suppliers dropdown STILL EXISTS (only Purchase Orders inside)
import { FaStore, FaComments } from "react-icons/fa"; // ✅ ADDED: FaComments for Chats
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdDashboard } from "react-icons/md";
import { HiCurrencyDollar } from "react-icons/hi";
import { FaInfo } from "react-icons/fa";
import { IoMdCart } from "react-icons/io";
import { FaBoxOpen } from "react-icons/fa";
import { RiFileListLine } from "react-icons/ri";
import { MdPostAdd } from "react-icons/md";
import { FaUsersSlash } from "react-icons/fa";
import { FaUserAlt } from "react-icons/fa";
import { FaTags } from "react-icons/fa";
import { FaBoxes } from "react-icons/fa";
import { FaEnvelope } from "react-icons/fa";
import { FaReceipt } from "react-icons/fa";
import { FaShoppingCart } from "react-icons/fa";
import { FaTruck } from "react-icons/fa";
import { FaBarcode } from "react-icons/fa";
import { FaUserCircle } from "react-icons/fa";
import { FaSignOutAlt } from "react-icons/fa";
import { FaBars } from "react-icons/fa";
import { FaUserPlus } from "react-icons/fa";
import { FaBuilding } from "react-icons/fa";
import { BsListStars } from "react-icons/bs";
import { FaClipboardList } from "react-icons/fa";
import { FaChartSimple } from "react-icons/fa6";
import { FaRegAddressBook } from "react-icons/fa";
import { TbBrandCodepen } from "react-icons/tb";
import { VscTypeHierarchy } from "react-icons/vsc";
import { GiValley } from "react-icons/gi";
import { BsPatchQuestionFill } from "react-icons/bs";
import { SiAlipay } from "react-icons/si";
import { FaPlusCircle } from "react-icons/fa";
import { IoImagesSharp } from "react-icons/io5";
import { MdOutlineProductionQuantityLimits } from "react-icons/md";
import { SiNotion } from "react-icons/si";
import { FaBoxTissue } from "react-icons/fa6";
import { FaFileImage } from "react-icons/fa";
import { GiBorderedShield } from "react-icons/gi";
import { GiAtomCore } from "react-icons/gi";
import { RiUserCommunityFill } from "react-icons/ri";
import { GiCoffeeBeans } from "react-icons/gi";
import { SiProteus } from "react-icons/si";
import { SiGooglecampaignmanager360 } from "react-icons/si";
import { TiArrowSortedDown } from "react-icons/ti";

import { subscribeChatSummary } from '../utils/chatSummary';
import { getStoredUser, isSuperAdmin as checkSuperAdmin, canAccessPath } from '../utils/permissions';

import './Sidebar.css';

// small red number shown next to Communication / Chats while customers wait for a person
const badgeStyle = {
  marginLeft: 8, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: '#e53935',
  color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const Sidebar = () => {
  const [chatWaiting, setChatWaiting] = useState(0);
  useEffect(() => subscribeChatSummary((s) => setChatWaiting(s.waiting || 0)), []);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Role / permission helpers
  const currentUser = getStoredUser();
  const superAdmin = checkSuperAdmin(currentUser);
  const can = (path) => canAccessPath(currentUser, path);
  const canAny = (paths) => paths.some((p) => can(p));

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  // ✅ Get current shop ID from URL or localStorage
  const getCurrentShopId = () => {
    // ADMIN / STAFF are locked to their own shop
    if (!superAdmin && currentUser && currentUser.shop_id) {
      return String(currentUser.shop_id);
    }

    // 1. Check URL first
    const urlMatch = location.pathname.match(/\/shop\/(\d+)/);
    if (urlMatch) return urlMatch[1];

    // 2. Check localStorage
    return localStorage.getItem('selectedShopId')
        || localStorage.getItem('selected_shop_id')
        || null;
  };

  // ✅ Handle navigation - ALWAYS preserve shop ID
  const handleNavigation = (path) => {
    const shopId = getCurrentShopId();

    // Remove leading slash if exists
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    if (shopId && cleanPath) {
      // ✅ Include shop ID in URL
      navigate(`/shop/${shopId}/${cleanPath}`);
    } else if (shopId && !cleanPath) {
      // Dashboard - just shop ID
      navigate(`/shop/${shopId}`);
    } else {
      navigate(path);
    }

    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  // ✅ isActive check - compare path without shop prefix
  const isActive = (path) => {
    // Get path without /shop/:shopId prefix
    const currentPath = location.pathname.replace(/^\/shop\/\d+/, '') || '/';
    const comparePath = path.startsWith('/') ? path : `/${path}`;
    return currentPath === comparePath;
  };

  const toggleSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <>
      {isMobile && !isMobileOpen && (
        <button type="button" className="mobile-toggle-btn" onClick={toggleSidebar}>
          <FaBars />
        </button>
      )}

      {isMobile && isMobileOpen && (
        <div
          className={`sidebar-overlay ${isMobileOpen ? 'active' : ''}`}
          onClick={toggleSidebar}
        />
      )}

      <div className={`sidebar ${isMobile ? 'mobile-sidebar' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/image.png" alt="HIRA-TECH Logo" className="logo" />
          </div>
          {isMobile && (
            <button type="button" className="mobile-close-btn" onClick={toggleSidebar}>
              ×
            </button>
          )}
        </div>

        <div className="sidebar-menu">
          <ul className="menu-items">
            {/* Dashboard */}
            {can('/') && (
              <li className="menu-item">
                <div
                  className={`menu-link ${isActive('/') ? 'active' : ''}`}
                  onClick={() => handleNavigation('/')}
                >
                  <MdDashboard className="menu-icon" />
                  <span>Dashboard</span>
                </div>
              </li>
            )}

            {/* ====== USERS DROPDOWN ====== */}
            {canAny(['/users', '/company-addresses', '/shops', '/staff', '/customers', '/inactive-customers']) && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Users' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Users')}
                >
                  <div className="menu-link-content">
                    <RiUserCommunityFill className="menu-icon" />
                    <span>Users</span>
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Users' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Users' && (
                  <ul className="submenu">
                    {can('/users') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/users') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/users')}
                        >
                          <FaUserPlus className="submenu-icon" />
                          <span>Users List</span>
                        </div>
                      </li>
                    )}
                    {can('/company-addresses') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/company-addresses') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/company-addresses')}
                        >
                          <FaBuilding className="submenu-icon" />
                          <span>Company Addresses</span>
                        </div>
                      </li>
                    )}
                    {can('/shops') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/shops') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/shops')}
                        >
                          <FaStore className="submenu-icon" />
                          <span>Shop</span>
                        </div>
                      </li>
                    )}
                    {can('/staff') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/staff') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/staff')}
                        >
                          <FaUserCircle className="submenu-icon" />
                          <span>Staff Members</span>
                        </div>
                      </li>
                    )}
                    {can('/customers') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/customers') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/customers')}
                        >
                          <FaUserAlt className="submenu-icon" />
                          <span>Customers</span>
                        </div>
                      </li>
                    )}
                    {can('/inactive-customers') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/inactive-customers') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/inactive-customers')}
                        >
                          <FaUsersSlash className="submenu-icon" />
                          <span>Inactive Customers</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Currency Settings */}
            {can('/currency-settings') && (
              <li className="menu-item">
                <div
                  className={`menu-link ${isActive('/currency-settings') ? 'active' : ''}`}
                  onClick={() => handleNavigation('/currency-settings')}
                >
                  <HiCurrencyDollar className="menu-icon" />
                  <span>Currency Settings</span>
                </div>
              </li>
            )}

            {/* ====== ORDERS DROPDOWN ====== */}
            {canAny(['/Addresss', '/invoice-details', '/Customerspayment', '/Ordernotes', '/OrderstatusImage', '/Orders-product', '/Payments', '/place-orders', '/orders']) && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Orders' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Orders')}
                >
                  <div className="menu-link-content">
                    <GiAtomCore className="menu-icon" />
                    <span>Orders</span>
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Orders' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Orders' && (
                  <ul className="submenu">
                    {can('/Addresss') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Addresss') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Addresss')}
                        >
                          <FaRegAddressBook className="submenu-icon" />
                          <span>Address</span>
                        </div>
                      </li>
                    )}
                    {can('/invoice-details') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/invoice-details') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/invoice-details')}
                        >
                          <FaInfo className="submenu-icon" />
                          <span>Invoice Details</span>
                        </div>
                      </li>
                    )}
                                        {/* ❌ COMMENTED OUT: Customer Payment Link */}
                    {/* {can('/Customerspayment') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Customerspayment') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Customerspayment')}
                        >
                          <FaPlusCircle className="submenu-icon" />
                          <span>Customer payment</span>
                        </div>
                      </li>
                    )} */}

                    {/* ❌ COMMENTED OUT: Order Notes Link */}
                    {/* {can('/Ordernotes') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Ordernotes') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Ordernotes')}
                        >
                          <GiBorderedShield className="submenu-icon" />
                          <span>Order Notes</span>
                        </div>
                      </li>
                    )} */}

                    {can('/OrderstatusImage') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/OrderstatusImage') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/OrderstatusImage')}
                        >
                          <FaFileImage className="submenu-icon" />
                          <span>Order status Image</span>
                        </div>
                      </li>
                    )}
                    {can('/Orders-product') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Orders-product') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Orders-product')}
                        >
                          <MdOutlineProductionQuantityLimits className="submenu-icon" />
                          <span>Ordered product</span>
                        </div>
                      </li>
                    )}
                    {can('/Payments') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Payments') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Payments')}
                        >
                          <SiAlipay className="submenu-icon" />
                          <span>Payments</span>
                        </div>
                      </li>
                    )}
                    {can('/place-orders') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/place-orders') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/place-orders')}
                        >
                          <IoMdCart className="submenu-icon" />
                          <span>Place Orders</span>
                        </div>
                      </li>
                    )}
                    {can('/orders') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/orders') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/orders')}
                        >
                          <FaBoxOpen className="submenu-icon" />
                          <span>Orders</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* ====== FINANCE DROPDOWN (Postings + Suppliers MOVED HERE) ====== */}
            {superAdmin && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Finance' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Finance')}
                >
                  <div className="menu-link-content">
                    <GiCoffeeBeans className="menu-icon" />
                    <span>Finance</span>
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Finance' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Finance' && (
                  <ul className="submenu">
                    <li>
                      <div
                        className={`submenu-link ${isActive('/bills') ? 'active' : ''}`}
                        onClick={() => handleNavigation('/bills')}
                      >
                        <FaReceipt className="submenu-icon" />
                        <span>Bills</span>
                      </div>
                    </li>
                    <li>
                      <div
                        className={`submenu-link ${isActive('/statements') ? 'active' : ''}`}
                        onClick={() => handleNavigation('/statements')}
                      >
                        <RiFileListLine className="submenu-icon" />
                        <span>Statements</span>
                      </div>
                    </li>
                    <li>
                      <div
                        className={`submenu-link ${isActive('/expenses') ? 'active' : ''}`}
                        onClick={() => handleNavigation('/expenses')}
                      >
                        <FaShoppingCart className="submenu-icon" />
                        <span>Expenses</span>
                      </div>
                    </li>
                    {/* ✅ Postings moved here from Communication */}
                    {can('/postings') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/postings') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/postings')}
                        >
                          <MdPostAdd className="submenu-icon" />
                          <span>Postings</span>
                        </div>
                      </li>
                    )}
                    {/* ✅ Suppliers field moved here from Suppliers dropdown */}
                    {can('/suppliers') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/suppliers') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/suppliers')}
                        >
                          <FaTruck className="submenu-icon" />
                          <span>Suppliers</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* ====== PRODUCTS DROPDOWN ====== */}
            {canAny(['/products', '/productlist', '/Imagesgallery', '/Implant-companys', '/Brands', '/categories', '/Types', '/Variations']) && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Products' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Products')}
                >
                  <div className="menu-link-content">
                    <SiProteus className="menu-icon" />
                    <span>Products</span>
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Products' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Products' && (
                  <ul className="submenu">
                    {can('/products') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/products') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/products')}
                        >
                          <FaBoxes className="submenu-icon" />
                          <span>Products</span>
                        </div>
                      </li>
                    )}
                    {can('/productlist') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/productlist') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/productlist')}
                        >
                          <FaClipboardList className="submenu-icon" />
                          <span>Product List</span>
                        </div>
                      </li>
                    )}
                    {can('/Imagesgallery') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Imagesgallery') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Imagesgallery')}
                        >
                          <IoImagesSharp className="submenu-icon" />
                          <span>Image gallery</span>
                        </div>
                      </li>
                    )}
                    {can('/Implant-companys') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Implant-companys') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Implant-companys')}
                        >
                          <FaChartSimple className="submenu-icon" />
                          <span>Implant Company</span>
                        </div>
                      </li>
                    )}
                    {can('/Brands') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Brands') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Brands')}
                        >
                          <TbBrandCodepen className="submenu-icon" />
                          <span>Brands</span>
                        </div>
                      </li>
                    )}
                    {can('/categories') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/categories') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/categories')}
                        >
                          <FaTags className="submenu-icon" />
                          <span>Categories</span>
                        </div>
                      </li>
                    )}
                    {can('/Types') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Types') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Types')}
                        >
                          <VscTypeHierarchy className="submenu-icon" />
                          <span>Types</span>
                        </div>
                      </li>
                    )}
                    {can('/Variations') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Variations') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Variations')}
                        >
                          <GiValley className="submenu-icon" />
                          <span>Variations</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* ====== COMMUNICATION DROPDOWN (Postings moved out) ====== */}
            {canAny(['/messages', '/Questions', '/reviews', '/Chats']) && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Communication' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Communication')}
                >
                  <div className="menu-link-content">
                    <FaEnvelope className="menu-icon" />
                    <span>Communication</span>
                    {chatWaiting > 0 && <span style={badgeStyle}>{chatWaiting}</span>}
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Communication' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Communication' && (
                  <ul className="submenu">
                    {can('/messages') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/messages') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/messages')}
                        >
                          <FaEnvelope className="submenu-icon" />
                          <span>Messages</span>
                        </div>
                      </li>
                    )}
                    {can('/Chats') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Chats') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Chats')}
                        >
                          <FaComments className="submenu-icon" />
                          <span>Chats</span>
                          {chatWaiting > 0 && <span style={badgeStyle}>{chatWaiting}</span>}
                        </div>
                      </li>
                    )}
                    {can('/Questions') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Questions') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Questions')}
                        >
                          <BsPatchQuestionFill className="submenu-icon" />
                          <span>Questions</span>
                        </div>
                      </li>
                    )}
                    {can('/reviews') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/reviews') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/reviews')}
                        >
                          <BsListStars className="submenu-icon" />
                          <span>Reviews</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* ❌ FEEDBACK SECTION REMOVED */}
            {/* ❌ SEPARATE "EXPENSE" DROPDOWN REMOVED */}

            {/* ====== SUPPLIERS (PURCHASE) DROPDOWN - COMMENTED OUT ====== */}
            {/*
            {canAny(['/suppliers', '/purchase-orders']) && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Suppliers' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Suppliers')}
                >
                  <div className="menu-link-content">
                    <FaTruck className="menu-icon" />
                    <span>Purchase</span>
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Suppliers' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Suppliers' && (
                  <ul className="submenu">
                    {can('/purchase-orders') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/purchase-orders') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/purchase-orders')}
                        >
                          <FaClipboardList className="submenu-icon" />
                          <span>Purchase Orders</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}
            */}

            {/* Lot Tracking */}
            {can('/lot-tracking') && (
              <li className="menu-item">
                <div
                  className={`menu-link ${isActive('/lot-tracking') ? 'active' : ''}`}
                  onClick={() => handleNavigation('/lot-tracking')}
                >
                  <FaBarcode className="menu-icon" />
                  <span>Lot Tracking</span>
                </div>
              </li>
            )}

            {/* Profile */}
            {can('/profile') && (
              <li className="menu-item">
                <div
                  className={`menu-link ${isActive('/profile') ? 'active' : ''}`}
                  onClick={() => handleNavigation('/profile')}
                >
                  <FaUserCircle className="menu-icon" />
                  <span>Profile</span>
                </div>
              </li>
            )}

            {/* ====== MANAGEMENT SYSTEM DROPDOWN ====== */}
            {canAny(['/Manufacturingdata', '/Issuestock', '/StockMaterial', '/Notes']) && (
              <li className="menu-item dropdown">
                <div
                  className={`menu-link dropdown-toggle ${activeMenu === 'Manufacturing' ? 'active-dropdown' : ''}`}
                  onClick={() => toggleMenu('Manufacturing')}
                >
                  <div className="menu-link-content">
                    <SiGooglecampaignmanager360 className="menu-icon" />
                    <span>Management System</span>
                  </div>
                  <TiArrowSortedDown className={`dropdown-arrow-large ${activeMenu === 'Manufacturing' ? 'arrow-active' : ''}`} />
                </div>
                {activeMenu === 'Manufacturing' && (
                  <ul className="submenu">
                    {can('/Manufacturingdata') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Manufacturingdata') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Manufacturingdata')}
                        >
                          <FaBarcode className="submenu-icon" />
                          <span>Manufacturing data Record</span>
                        </div>
                      </li>
                    )}
                    {can('/Issuestock') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Issuestock') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Issuestock')}
                        >
                          <FaBoxTissue className="submenu-icon" />
                          <span>Issue Stocks</span>
                        </div>
                      </li>
                    )}
                    {can('/StockMaterial') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/StockMaterial') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/StockMaterial')}
                        >
                          <FaBoxes className="submenu-icon" />
                          <span>Stock Material</span>
                        </div>
                      </li>
                    )}
                    {can('/Notes') && (
                      <li>
                        <div
                          className={`submenu-link ${isActive('/Notes') ? 'active' : ''}`}
                          onClick={() => handleNavigation('/Notes')}
                        >
                          <SiNotion className="submenu-icon" />
                          <span>Notes</span>
                        </div>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </div>

        {/* Log Out */}
        <div className="sidebar-footer">
          <li className="menu-item logout-item">
            <div
              className="menu-link logout-link"
              onClick={() => handleNavigation('/logout')}
            >
              <FaSignOutAlt className="menu-icon" />
              <span>Log Out</span>
            </div>
          </li>
        </div>
      </div>
    </>
  );
};

export default Sidebar;